type FormValues = Record<string, unknown>
type AttributionPayload = Record<string, string>
type GtagParamValue = string | number | boolean | (() => void) | undefined
type GtagParams = Record<string, GtagParamValue>

declare const process: {
  env: Record<string, string | undefined>
}

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
    fbq?: (...args: unknown[]) => void
  }
}

const STORAGE_KEY = "root_ad_attribution"
const FALLBACK_GOOGLE_ADS_ID = "AW-10883148639"
const FALLBACK_FORM_LABEL = "t7igCIDBmbkcEN_WvsUo"
const FALLBACK_CALL_LABEL = "V3tSCIPBmbkcEN_WvsUo"
const CLICK_ID_KEYS = ["gclid", "gbraid", "wbraid"]
const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
]

const ALL_ATTRIBUTION_KEYS = [...CLICK_ID_KEYS, ...UTM_KEYS]

const cleanEnvValue = (value?: string): string | undefined => {
  if (!value || value.startsWith("replace_")) return undefined
  return value
}

const getGoogleAdsId = () =>
  cleanEnvValue(process.env.GATSBY_GOOGLE_ADS_ID) || FALLBACK_GOOGLE_ADS_ID

const getConversionSendTo = (label?: string, fallbackLabel?: string) => {
  const cleanLabel = cleanEnvValue(label) || fallbackLabel
  if (!cleanLabel) return undefined

  return `${getGoogleAdsId()}/${cleanLabel}`
}

const getWindow = () => (typeof window === "undefined" ? undefined : window)

const readStoredAttribution = (): AttributionPayload => {
  const activeWindow = getWindow()
  if (!activeWindow) return {}

  try {
    const storedValue = activeWindow.localStorage.getItem(STORAGE_KEY)
    return storedValue ? JSON.parse(storedValue) : {}
  } catch {
    return {}
  }
}

const writeStoredAttribution = (payload: AttributionPayload) => {
  const activeWindow = getWindow()
  if (!activeWindow) return

  try {
    activeWindow.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch {
    // Browsers can block storage in privacy modes; tracking should fail open.
  }
}

const getCurrentPageMetadata = (): AttributionPayload => {
  const activeWindow = getWindow()
  if (!activeWindow) return {}

  return {
    form_page: activeWindow.location.href,
    referrer: activeWindow.document.referrer || "",
  }
}

export const captureAdAttribution = () => {
  const activeWindow = getWindow()
  if (!activeWindow) return

  const params = new URLSearchParams(activeWindow.location.search)
  const currentAttribution = ALL_ATTRIBUTION_KEYS.reduce<AttributionPayload>(
    (payload, key) => {
      const value = params.get(key)
      if (value) payload[key] = value
      return payload
    },
    {}
  )

  if (!Object.keys(currentAttribution).length) return

  writeStoredAttribution({
    ...readStoredAttribution(),
    ...currentAttribution,
    landing_page: activeWindow.location.href,
    referrer: activeWindow.document.referrer || "",
    ad_attribution_captured_at: new Date().toISOString(),
  })
}

export const getAdAttributionPayload = (): AttributionPayload => {
  const storedAttribution = readStoredAttribution()
  const currentPageMetadata = getCurrentPageMetadata()

  return Object.entries({
    ...currentPageMetadata,
    ...storedAttribution,
    landing_page: storedAttribution.landing_page || currentPageMetadata.form_page,
    referrer: storedAttribution.referrer || currentPageMetadata.referrer,
  }).reduce<AttributionPayload>((payload, [key, value]) => {
    if (value) payload[key] = value
    return payload
  }, {})
}

const normalizeFieldKey = (key: string) =>
  key.replace(/[^a-z0-9]/gi, "").toLowerCase()

const getStringFieldValue = (values: FormValues, candidateKeys: string[]) => {
  for (const key of candidateKeys) {
    const value = values[key]
    if (typeof value === "string" && value.trim()) return value
  }

  const normalizedCandidates = candidateKeys.map(normalizeFieldKey)
  const matchedEntry = Object.entries(values).find(([key, value]) => {
    if (typeof value !== "string" || !value.trim()) return false
    const normalizedKey = normalizeFieldKey(key)
    return normalizedCandidates.some((candidate) =>
      normalizedKey.includes(candidate)
    )
  })

  return typeof matchedEntry?.[1] === "string" ? matchedEntry[1] : undefined
}

const normalizeEmail = (value: unknown) =>
  typeof value === "string" ? value.trim().toLowerCase() : undefined

const normalizePhone = (value: unknown) => {
  if (typeof value !== "string") return undefined

  const digits = value.replace(/\D/g, "")
  if (digits.length === 10) return `+1${digits}`
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`
  return value.trim() || undefined
}

const setEnhancedConversionUserData = (values: FormValues) => {
  const activeWindow = getWindow()
  if (!activeWindow?.gtag) return

  const email = normalizeEmail(
    getStringFieldValue(values, ["email", "Email", "emailAddress"])
  )
  const phoneNumber = normalizePhone(
    getStringFieldValue(values, ["phone", "Phone", "phoneNumber"])
  )
  const userData: Record<string, string> = {}

  if (email) userData.email = email
  if (phoneNumber) userData.phone_number = phoneNumber
  if (!Object.keys(userData).length) return

  activeWindow.gtag("set", "user_data", userData)
}

const trackGtagEvent = (eventName: string, params: GtagParams) => {
  const activeWindow = getWindow()
  if (!activeWindow?.gtag) return

  activeWindow.gtag("event", eventName, params)
}

// Meta pixel events colocate with the Google conversion triggers so the two
// stacks can never disagree about what counts as a lead. The pixel base code
// (init) lives in gatsby-ssr.tsx; PageView fires from gatsby-browser.tsx.
// eventID rides the SAME transaction id as the Ads conversion — required so a
// future Conversions-API (server) send of the same event dedupes cleanly.
const trackMetaEvent = (eventName: "Lead" | "Contact", eventId: string) => {
  const activeWindow = getWindow()
  if (!activeWindow?.fbq) return

  activeWindow.fbq("track", eventName, {}, { eventID: eventId })
}

export const trackMetaPageView = () => {
  const activeWindow = getWindow()
  if (!activeWindow?.fbq) return

  activeWindow.fbq("track", "PageView")
}

const runOnce = (callback: () => void) => {
  let hasRun = false

  return () => {
    if (hasRun) return
    hasRun = true
    callback()
  }
}

const trackConversionWithCallback = ({
  params,
  onComplete,
}: {
  params: GtagParams
  onComplete?: () => void
}) => {
  const activeWindow = getWindow()
  if (!activeWindow?.gtag) {
    onComplete?.()
    return
  }

  if (!onComplete) {
    trackGtagEvent("conversion", params)
    return
  }

  const complete = runOnce(onComplete)
  const timeoutId = activeWindow.setTimeout(complete, 800)

  activeWindow.gtag("event", "conversion", {
    ...params,
    event_callback: () => {
      activeWindow.clearTimeout(timeoutId)
      complete()
    },
    event_timeout: 800,
  })
}

export const trackQuoteFormLead = ({
  values,
  formMachineName,
  validationSchema,
  onComplete,
}: {
  values: FormValues
  formMachineName: string
  validationSchema: string
  onComplete?: () => void
}) => {
  const transactionId = `web-form-${formMachineName}-${Date.now()}`
  const legacyEvent =
    validationSchema === "Get a commercial quote"
      ? {
          name: "generate_commercial_lead_success",
          value: 600,
        }
      : validationSchema === "Get a quote"
      ? {
          name: "generate_residential_lead_success",
          value: 130,
        }
      : undefined

  setEnhancedConversionUserData(values)
  trackMetaEvent("Lead", transactionId)

  if (legacyEvent) {
    trackGtagEvent(legacyEvent.name, {
      value: legacyEvent.value,
      currency: "USD",
      transaction_id: transactionId,
    })
  }

  const sendTo = getConversionSendTo(
    process.env.GATSBY_GOOGLE_ADS_FORM_LABEL,
    FALLBACK_FORM_LABEL
  )
  if (!sendTo) {
    onComplete?.()
    return
  }

  trackConversionWithCallback({
    params: {
      send_to: sendTo,
      value: 0,
      currency: "USD",
      transaction_id: transactionId,
      form_name: formMachineName,
    },
    onComplete,
  })
}

export const trackPhoneLeadAndNavigate = ({
  phoneHref,
  eventName = "called_us_residential",
  value = 126,
}: {
  phoneHref: string
  eventName?: "called_us_residential" | "called_us_commercial"
  value?: number
}) => {
  const transactionId = `phone-click-${Date.now()}`

  trackMetaEvent("Contact", transactionId)
  trackGtagEvent(eventName, {
    value,
    currency: "USD",
    transaction_id: transactionId,
  })

  const sendTo = getConversionSendTo(
    process.env.GATSBY_GOOGLE_ADS_CALL_LABEL,
    FALLBACK_CALL_LABEL
  )
  if (sendTo) {
    trackConversionWithCallback({
      params: {
        send_to: sendTo,
        value: 0,
        currency: "USD",
        transaction_id: transactionId,
      },
      onComplete: () => {
        const activeWindow = getWindow()
        if (activeWindow) activeWindow.location.href = phoneHref
      },
    })
    return
  }

  const activeWindow = getWindow()
  if (activeWindow) activeWindow.location.href = phoneHref
}
